import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiFolder,
  FiGitBranch,
  FiPlus,
  FiRefreshCcw,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import CategoryFilters from "./CategoryFilters";
import CategoryTable from "./CategoryTable";
import AddEditCategory from "./AddEditCategory";

import "./CategoryManagement.css";

/* =========================================================
   FILTER DEFAULTS
========================================================= */

const EMPTY_FILTERS = {
  search: "",
  status: "",
  parentCategory: "",
  level: "",
  rootOnly: false,
};

/* =========================================================
   PAGINATION DEFAULTS
========================================================= */

const EMPTY_PAGINATION = {
  total: 0,

  totalRecords: 0,

  page: 1,

  limit: 20,

  totalPages: 1,
};

/* =========================================================
   EXTRACT CATEGORY LIST RESPONSE
========================================================= */

const extractCategoryResponse = (
  response
) => {
  let records = [];

  let pagination = null;

  /*
   * Response:
   *
   * {
   *   status: "success",
   *   data: [...]
   * }
   */

  if (
    Array.isArray(
      response?.data
    )
  ) {
    records =
      response.data;

    pagination =
      response?.pagination ||
      null;
  }

  /*
   * Response:
   *
   * {
   *   status: "success",
   *   data: {
   *     categories: [],
   *     pagination: {}
   *   }
   * }
   */

  else if (
    Array.isArray(
      response?.data
        ?.categories
    )
  ) {
    records =
      response.data
        .categories;

    pagination =
      response.data
        .pagination ||
      response?.pagination ||
      null;
  }

  /*
   * Other safe response.
   */

  else if (
    Array.isArray(
      response?.categories
    )
  ) {
    records =
      response.categories;

    pagination =
      response.pagination ||
      null;
  }

  return {
    records,

    pagination,
  };
};

/* =========================================================
   COMPONENT
========================================================= */

const CategoryManagement =
  () => {
    const api =
      useMemo(
        () =>
          new API(),
        []
      );

    const swal =
      useSwal();

    /* =====================================================
       DATA
    ===================================================== */

    const [
      categories,
      setCategories,
    ] =
      useState([]);

    /* =====================================================
       FILTERS

       filters:
       Current UI values.

       appliedFilters:
       Values actually sent to API.

       This allows user to type/change filters without
       automatically requesting the API until Search.
    ===================================================== */

    const [
      filters,
      setFilters,
    ] =
      useState({
        ...EMPTY_FILTERS,
      });

    const [
      appliedFilters,
      setAppliedFilters,
    ] =
      useState({
        ...EMPTY_FILTERS,
      });

    /* =====================================================
       PAGINATION
    ===================================================== */

    const [
      pagination,
      setPagination,
    ] =
      useState({
        ...EMPTY_PAGINATION,
      });

    /* =====================================================
       REQUEST STATE
    ===================================================== */

    const [
      loading,
      setLoading,
    ] =
      useState(false);

    const [
      error,
      setError,
    ] =
      useState("");

    /* =====================================================
       FORM STATE
    ===================================================== */

    const [
      formOpen,
      setFormOpen,
    ] =
      useState(false);

    const [
      editingCategory,
      setEditingCategory,
    ] =
      useState(null);

    /*
     * Used when clicking:
     *
     * + Child
     *
     * Example:
     *
     * Laptop
     *   ↓
     * Business Laptop
     *   ↓
     * Dell
     *
     * If "+ Child" is clicked on Dell:
     *
     * parentForNewCategory = Dell
     */

    const [
      parentForNewCategory,
      setParentForNewCategory,
    ] =
      useState(null);

    /* =====================================================
       FETCH CATEGORIES
    ===================================================== */

    const fetchCategories =
      useCallback(
        async () => {
          try {
            setLoading(
              true
            );

            setError("");

            /* =============================================
               LEVEL

               IMPORTANT:

               Do NOT use:

               appliedFilters.level || ""

               because:

               level = 0

               is a valid hierarchy level.

               We explicitly check empty string instead.
            ============================================= */

            const selectedLevel =
              appliedFilters.level ===
                "" ||
              appliedFilters.level ===
                undefined ||
              appliedFilters.level ===
                null
                ? ""
                : Number(
                    appliedFilters.level
                  );

            /* =============================================
               REQUEST PAYLOAD
            ============================================= */

            const payload = {
              page:
                pagination.page,

              limit:
                pagination.limit,

              search:
                String(
                  appliedFilters
                    .search ||
                    ""
                ).trim(),

              status:
                appliedFilters
                  .status ||
                "",

              parentCategory:
                appliedFilters
                  .parentCategory ||
                "",

              level:
                selectedLevel,

              rootOnly:
                Boolean(
                  appliedFilters
                    .rootOnly
                ),
            };

            const response =
              await api.post(
                config
                  .getInventoryCategories,

                payload
              );

            /* =============================================
               SUCCESS CHECK
            ============================================= */

            if (
              response?.status !==
                "success" &&
              response?.success !==
                true
            ) {
              throw new Error(
                response?.message ||
                  "Unable to fetch categories."
              );
            }

            /* =============================================
               EXTRACT RESPONSE
            ============================================= */

            const {
              records,
              pagination:
                apiPagination,
            } =
              extractCategoryResponse(
                response
              );

            setCategories(
              records
            );

            /* =============================================
               PAGINATION RESPONSE
            ============================================= */

            setPagination(
              (
                previous
              ) => {
                const totalRecords =
                  Number(
                    apiPagination
                      ?.totalRecords ??
                      apiPagination
                        ?.total ??
                      response
                        ?.pagination
                        ?.totalRecords ??
                      response
                        ?.pagination
                        ?.total ??
                      records.length ??
                      0
                  );

                const currentPage =
                  Number(
                    apiPagination
                      ?.page ??
                      response
                        ?.pagination
                        ?.page ??
                      previous.page
                  );

                const currentLimit =
                  Number(
                    apiPagination
                      ?.limit ??
                      response
                        ?.pagination
                        ?.limit ??
                      previous.limit
                  );

                const calculatedPages =
                  Math.ceil(
                    totalRecords /
                      Math.max(
                        currentLimit,
                        1
                      )
                  );

                const totalPages =
                  Math.max(
                    Number(
                      apiPagination
                        ?.totalPages ??
                        response
                          ?.pagination
                          ?.totalPages ??
                        calculatedPages ??
                        1
                    ),

                    1
                  );

                return {
                  ...previous,

                  total:
                    totalRecords,

                  totalRecords,

                  page:
                    currentPage,

                  limit:
                    currentLimit,

                  totalPages,
                };
              }
            );
          } catch (
            requestError
          ) {
            console.error(
              "Get inventory categories error:",
              requestError
            );

            setCategories([]);

            setPagination(
              (
                previous
              ) => ({
                ...previous,

                total: 0,

                totalRecords: 0,

                totalPages: 1,
              })
            );

            setError(
              requestError
                ?.response
                ?.data
                ?.message ||
                requestError
                  ?.message ||
                "Unable to fetch categories."
            );
          } finally {
            setLoading(false);
          }
        },
        [
          api,

          appliedFilters,

          pagination.page,

          pagination.limit,
        ]
      );

    /* =====================================================
       INITIAL LOAD /
       FILTER CHANGE /
       PAGINATION CHANGE
    ===================================================== */

    useEffect(
      () => {
        fetchCategories();
      },
      [
        fetchCategories,
      ]
    );

    /* =====================================================
       SEARCH
    ===================================================== */

    const handleSearch =
      () => {
        /*
         * Apply current UI filters.
         */

        setAppliedFilters({
          ...filters,
        });

        /*
         * Search always starts from page 1.
         */

        setPagination(
          (
            previous
          ) => ({
            ...previous,

            page: 1,
          })
        );
      };

    /* =====================================================
       RESET FILTERS
    ===================================================== */

    const handleReset =
      () => {
        setFilters({
          ...EMPTY_FILTERS,
        });

        setAppliedFilters({
          ...EMPTY_FILTERS,
        });

        setPagination(
          (
            previous
          ) => ({
            ...previous,

            page: 1,
          })
        );
      };

    /* =====================================================
       ADD ROOT CATEGORY
    ===================================================== */

    const handleAddCategory =
      () => {
        /*
         * Create mode.
         */

        setEditingCategory(
          null
        );

        /*
         * No parent.
         *
         * Backend should therefore create:
         *
         * parentCategory = null
         * level = 0
         */

        setParentForNewCategory(
          null
        );

        setFormOpen(true);
      };

    /* =====================================================
       ADD CHILD CATEGORY

       IMPORTANT:

       Works from EVERY hierarchy level.

       Laptop
         ↓
       Business Laptop
         ↓
       Dell
         ↓
       Latitude
         ↓
       5000 Series
         ↓
       ...
    ===================================================== */

    const handleAddChild =
      (
        parentCategory
      ) => {
        if (
          !parentCategory?._id
        ) {
          return;
        }

        /*
         * CREATE mode.
         */

        setEditingCategory(
          null
        );

        /*
         * Selected row becomes parent.
         */

        setParentForNewCategory(
          parentCategory
        );

        setFormOpen(true);
      };

    /* =====================================================
       EDIT CATEGORY
    ===================================================== */

    const handleEditCategory =
      (
        category
      ) => {
        if (
          !category?._id
        ) {
          return;
        }

        /*
         * We are editing,
         * not creating child.
         */

        setParentForNewCategory(
          null
        );

        setEditingCategory(
          category
        );

        setFormOpen(true);
      };

    /* =====================================================
       CLOSE FORM
    ===================================================== */

    const handleCloseForm =
      () => {
        setFormOpen(false);

        setEditingCategory(
          null
        );

        setParentForNewCategory(
          null
        );
      };

    /* =====================================================
       CREATE / UPDATE SUCCESS
    ===================================================== */

    const handleFormSuccess =
      () => {
        setFormOpen(false);

        setEditingCategory(
          null
        );

        setParentForNewCategory(
          null
        );

        /*
         * Refresh table after:
         *
         * CREATE
         * UPDATE
         */

        fetchCategories();
      };

    /* =====================================================
       DELETE CATEGORY
    ===================================================== */

    const handleDeleteCategory =
      async (
        category
      ) => {
        if (
          !category?._id
        ) {
          return;
        }

        const categoryName =
          category?.name ||
          "this category";

        const confirmation =
          await swal.warning({
            icon:
              "warning",

            title:
              "Delete Category?",

            html: `
              <div style="text-align:center">

                <p style="margin:0 0 8px">
                  Are you sure you want to delete
                  <strong>
                    ${categoryName}
                  </strong>?
                </p>

                <small style="
                  color:#667085;
                  line-height:1.5;
                  display:block;
                ">
                  Categories containing subcategories
                  cannot be deleted.
                </small>

              </div>
            `,

            showCancelButton:
              true,

            confirmButtonText:
              "Yes, Delete",

            cancelButtonText:
              "Cancel",

            confirmButtonColor:
              "#dc2626",
          });

        if (
          !confirmation
            ?.isConfirmed
        ) {
          return;
        }

        try {
          setLoading(true);

          const response =
            await api.delete(
              config
                .deleteInventoryCategory(
                  category._id
                )
            );

          if (
            response?.status !==
              "success" &&
            response?.success !==
              true
          ) {
            throw new Error(
              response?.message ||
                "Unable to delete category."
            );
          }

          await swal.success({
            icon:
              "success",

            title:
              "Category Deleted",

            text:
              response?.message ||
              "Category deleted successfully.",

            timer:
              1400,

            showConfirmButton:
              false,
          });

          /*
           * If deleting the last item
           * from page > 1,
           * go to previous page.
           */

          if (
            categories.length ===
              1 &&
            pagination.page > 1
          ) {
            setPagination(
              (
                previous
              ) => ({
                ...previous,

                page:
                  previous.page -
                  1,
              })
            );
          } else {
            await fetchCategories();
          }
        } catch (
          requestError
        ) {
          console.error(
            "Delete category error:",
            requestError
          );

          await swal.error({
            icon:
              "error",

            title:
              "Delete Failed",

            text:
              requestError
                ?.response
                ?.data
                ?.message ||
              requestError
                ?.message ||
              "Unable to delete category.",
          });
        } finally {
          setLoading(false);
        }
      };

    /* =====================================================
       CHANGE CATEGORY STATUS
    ===================================================== */

    const handleChangeStatus =
      async (
        category,
        nextStatus
      ) => {
        if (
          !category?._id
        ) {
          return;
        }

        if (
          ![
            "active",
            "inactive",
          ].includes(
            nextStatus
          )
        ) {
          return;
        }

        try {
          setLoading(true);

          const response =
            await api.patch(
              config
                .changeInventoryCategoryStatus(
                  category._id
                ),

              {
                status:
                  nextStatus,
              }
            );

          if (
            response?.status !==
              "success" &&
            response?.success !==
              true
          ) {
            throw new Error(
              response?.message ||
                "Unable to update category status."
            );
          }

          await swal.success({
            icon:
              "success",

            title:
              nextStatus ===
              "active"
                ? "Category Activated"
                : "Category Deactivated",

            text:
              response?.message ||
              "Category status updated successfully.",

            timer:
              1200,

            showConfirmButton:
              false,
          });

          await fetchCategories();
        } catch (
          requestError
        ) {
          console.error(
            "Update category status error:",
            requestError
          );

          await swal.error({
            icon:
              "error",

            title:
              "Update Failed",

            text:
              requestError
                ?.response
                ?.data
                ?.message ||
              requestError
                ?.message ||
              "Unable to update category status.",
          });
        } finally {
          setLoading(false);
        }
      };

    /* =====================================================
       PAGE CHANGE
    ===================================================== */

    const handlePageChange =
      (
        page
      ) => {
        const targetPage =
          Number(page);

        if (
          !Number.isInteger(
            targetPage
          ) ||
          targetPage < 1 ||
          targetPage >
            pagination.totalPages ||
          targetPage ===
            pagination.page
        ) {
          return;
        }

        setPagination(
          (
            previous
          ) => ({
            ...previous,

            page:
              targetPage,
          })
        );
      };

    /* =====================================================
       PAGE SIZE
    ===================================================== */

    const handleLimitChange =
      (
        event
      ) => {
        const nextLimit =
          Number(
            event.target.value
          );

        if (
          ![
            10,
            20,
            50,
            100,
          ].includes(
            nextLimit
          )
        ) {
          return;
        }

        setPagination(
          (
            previous
          ) => ({
            ...previous,

            page: 1,

            limit:
              nextLimit,
          })
        );
      };

    /* =====================================================
       SUMMARY

       These two counts represent the currently loaded page.

       Total Categories comes from backend pagination.
    ===================================================== */

    const currentRootCount =
      useMemo(
        () =>
          categories.filter(
            (
              category
            ) =>
              Number(
                category?.level ??
                  0
              ) === 0
          ).length,
        [
          categories,
        ]
      );

    const currentNestedCount =
      useMemo(
        () =>
          categories.filter(
            (
              category
            ) =>
              Number(
                category?.level ??
                  0
              ) > 0
          ).length,
        [
          categories,
        ]
      );

    /* =====================================================
       RENDER
    ===================================================== */

    return (
      <div
        className="inventory-category-page"
      >
        {/* ===============================================
            HEADER
        =============================================== */}

        <div
          className="inventory-category-header"
        >
          <div>
            <span>
              CATEGORY MANAGEMENT
            </span>

            <h3>
              Inventory Categories
            </h3>

            <p>
              Create and manage unlimited nested
              category structures for products
              and your online storefront.
            </p>
          </div>

          <div
            className="inventory-category-header-actions"
          >
            <button
              type="button"
              className="inventory-category-refresh"
              onClick={
                fetchCategories
              }
              disabled={
                loading
              }
              title="Refresh categories"
            >
              <FiRefreshCcw
                className={
                  loading
                    ? "spinning"
                    : ""
                }
              />
            </button>

            <button
              type="button"
              className="inventory-category-add"
              onClick={
                handleAddCategory
              }
              disabled={
                loading
              }
            >
              <FiPlus />

              Add Category
            </button>
          </div>
        </div>

        {/* ===============================================
            SUMMARY
        =============================================== */}

        <div
          className="inventory-category-summary-grid"
        >
          <div
            className="inventory-category-summary"
          >
            <div>
              <FiFolder />
            </div>

            <span>
              Total Categories
            </span>

            <strong>
              {
                pagination.total
              }
            </strong>
          </div>

          <div
            className="
              inventory-category-summary
              inventory-category-summary-root
            "
          >
            <div>
              <FiFolder />
            </div>

            <span>
              Root Categories
            </span>

            <strong>
              {
                currentRootCount
              }
            </strong>
          </div>

          <div
            className="
              inventory-category-summary
              inventory-category-summary-nested
            "
          >
            <div>
              <FiGitBranch />
            </div>

            <span>
              Nested Categories
            </span>

            <strong>
              {
                currentNestedCount
              }
            </strong>
          </div>
        </div>

        {/* ===============================================
            FILTERS
        =============================================== */}

        <CategoryFilters
          filters={
            filters
          }

          setFilters={
            setFilters
          }

          loading={
            loading
          }

          onSearch={
            handleSearch
          }

          onReset={
            handleReset
          }
        />

        {/* ===============================================
            ERROR
        =============================================== */}

        {error ? (
          <div
            className="inventory-category-error"
          >
            {error}
          </div>
        ) : null}

        {/* ===============================================
            TABLE
        =============================================== */}

        <CategoryTable
          categories={
            categories
          }

          loading={
            loading
          }

          pagination={
            pagination
          }

          onEdit={
            handleEditCategory
          }

          onDelete={
            handleDeleteCategory
          }

          onChangeStatus={
            handleChangeStatus
          }

          onPageChange={
            handlePageChange
          }

          onLimitChange={
            handleLimitChange
          }

          onAddCategory={
            handleAddCategory
          }

          /*
           * IMPORTANT:
           *
           * Child can be created from
           * ANY hierarchy level.
           */

          onAddChild={
            handleAddChild
          }
        />

        {/* ===============================================
            ADD / EDIT CATEGORY
        =============================================== */}

        <AddEditCategory
          open={
            formOpen
          }

          category={
            editingCategory
          }

          /*
           * If user clicked:
           *
           * + Child
           *
           * this contains selected parent.
           */

          initialParentCategory={
            parentForNewCategory
          }

          onClose={
            handleCloseForm
          }

          onSuccess={
            handleFormSuccess
          }
        />
      </div>
    );
  };

export default CategoryManagement;